import { useState, useRef, useCallback } from 'react'
import * as XLSX from 'xlsx'
import { post, uploadFile } from '../../../../../utils/Api'
import { useGlobalContext } from '../../../../../utils/Context'
import FormLoader from '../../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'

interface Props {
  onClose: () => void
  onSuccess: () => void
}

type Mode = 'preview' | 'manual'
type Step = 'upload' | 'review' | 'done'

interface ReviewItem {
  trackingId: string
  newWeight: number
  amount: number | string
  type: 'manual' | 'automatic'
  description: string
  // from preview endpoint only
  oldWeight?: number
  oldAmount?: number
  newAmount?: number
  calculatedAmount?: number
  error?: string
}

interface FailedItem {
  trackingId: string
  newWeight?: number | string
  error: string
}

interface BulkResult {
  successful: { trackingId: string; invoiceId: string; amount: number }[]
  failed: { trackingId: string; error: string }[]
  totalProcessed: number
}

const cellStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '13px',
  whiteSpace: 'nowrap',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '4px 6px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '13px',
  boxSizing: 'border-box',
  minWidth: '90px',
}

function BulkUploadModal({ onClose, onSuccess }: Props) {
  const { notify } = useGlobalContext()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [mode, setMode] = useState<Mode>('preview')
  const [step, setStep] = useState<Step>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([])
  const [failedItems, setFailedItems] = useState<FailedItem[]>([])
  const [result, setResult] = useState<BulkResult | null>(null)

  // ─── File handling ───────────────────────────────────────────────────────────

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (selected) handleFile(selected)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const dropped = e.dataTransfer.files[0]
    if (dropped) handleFile(dropped)
  }

  const handleFile = (f: File) => {
    if (!f.name.match(/\.(xlsx|xls)$/i)) {
      notify('warn', 'Please upload an Excel file (.xlsx or .xls)')
      return
    }
    setFile(f)
  }

  const removeFile = () => {
    setFile(null)
    setReviewItems([])
    setFailedItems([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // ─── Mode 1: Auto Preview — send raw file to BE ───────────────────────────

  const handleAutoPreview = async () => {
    if (!file) return
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)

      const res = await uploadFile(
        formData,
        'admin/weight-discrepancy/preview-bulk',
      )

      if (res.status === 200) {
        const { successful, failed } = res.data

        const items: ReviewItem[] = (successful || []).map((item: any) => ({
          trackingId: item.trackingId,
          oldWeight: item.oldWeight,
          newWeight: item.newWeight,
          oldAmount: item.oldAmount,
          newAmount: item.newAmount,
          calculatedAmount: Number(item.difference.toFixed(2)),
          amount: Number(item.difference.toFixed(2)),
          type: 'automatic' as const,
          description:
            item.description ||
            `Weight changed from ${item.oldWeight}kg to ${item.newWeight}kg`,
        }))

        setReviewItems(items)
        setFailedItems(failed || [])
        setStep('review')
      } else {
        notify('error', res.message || 'Preview failed')
      }
    } catch {
      notify('error', 'Failed to generate preview')
    } finally {
      setLoading(false)
    }
  }

  // ─── Mode 2: Manual — parse Excel client-side ────────────────────────────

  const handleManualParse = () => {
    if (!file) return
    setLoading(true)

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: '',
        })

        if (rows.length < 2) {
          notify(
            'warn',
            'File must have a header row and at least one data row',
          )
          setLoading(false)
          return
        }

        // Skip header row
        const dataRows = rows.slice(1).filter((row) => row[0])
        const parsed: ReviewItem[] = dataRows.map((row) => {
          const trackingId = String(row[0] || '').trim()
          const newWeight = parseFloat(row[1]) || 0
          const amount = parseFloat(row[2]) || 0
          const type =
            String(row[3] || 'manual').toLowerCase() === 'automatic'
              ? 'automatic'
              : ('manual' as const)
          const description =
            String(row[4] || '').trim() || 'Bulk upload discrepancy'

          return { trackingId, newWeight, amount, type, description }
        })

        setReviewItems(parsed)
        setFailedItems([])
        setStep('review')
      } catch {
        notify('error', 'Failed to parse Excel file')
      } finally {
        setLoading(false)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  // ─── Edit a row field ────────────────────────────────────────────────────

  const updateItem = useCallback(
    (index: number, field: keyof ReviewItem, value: string | number) => {
      setReviewItems((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, [field]: value } : item,
        ),
      )
    },
    [],
  )

  const removeItem = useCallback((index: number) => {
    setReviewItems((prev) => prev.filter((_, i) => i !== index))
  }, [])

  // ─── Submit to record-bulk ───────────────────────────────────────────────

  const handleSubmit = async () => {
    const valid = reviewItems.filter(
      (r) =>
        r.trackingId &&
        r.newWeight > 0 &&
        String(r.amount).trim() !== '' &&
        !isNaN(Number(r.amount)),
    )

    if (valid.length === 0) {
      notify('warn', 'No valid rows to submit')
      return
    }

    setSubmitting(true)
    try {
      const res = await post(
        'admin/weight-discrepancy/record-bulk',
        {
          discrepancies: valid.map((r) => ({
            trackingId: r.trackingId,
            newWeight: r.newWeight,
            amount: Number(r.amount),
            type: 'manual',
            description: r.description,
          })),
        },
        null,
      )

      if (res.status === 200) {
        setResult(res.data)
        setStep('done')
        notify('success', `Processed ${res.data.totalProcessed} discrepancies`)
        onSuccess()
      } else {
        notify('error', res.message || 'Failed to record discrepancies')
      }
    } catch {
      notify('error', 'Failed to record discrepancies')
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Export Failed Items ─────────────────────────────────────────────────

  const exportFailedItems = (items: FailedItem[]) => {
    if (items.length === 0) return

    const data = items.map((item) => ({
      'Tracking ID': item.trackingId,
      'New Weight': item.newWeight || '',
      Error: item.error,
    }))

    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Failed Discrepancies')
    XLSX.writeFile(wb, 'failed_discrepancies.xlsx')
  }

  const exportProcessedItems = () => {
    if (reviewItems.length === 0) return

    const data = reviewItems.map((item) => ({
      'Tracking ID': item.trackingId,
      'Old Wt.': item.oldWeight || '',
      'New Wt.': item.newWeight,
      'Old Amt.': item.oldAmount || '',
      'New Amt.': item.newAmount || '',
      'Amount to Charge': item.amount,
      Description: item.description,
    }))

    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Processed Discrepancies')
    XLSX.writeFile(wb, 'processed_discrepancies.xlsx')
  }

  const removeZeroAmountItems = () => {
    const filtered = reviewItems.filter((item) => Number(item.amount) !== 0)
    setReviewItems(filtered)
    notify('success', `Removed ${reviewItems.length - filtered.length} items`)
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount)

  const validCount = reviewItems.filter(
    (r) => r.trackingId && r.newWeight > 0,
  ).length

  const invalidCount = reviewItems.filter(
    (r) => !r.trackingId || r.newWeight <= 0,
  ).length

  const isRowInvalid = (r: ReviewItem) => !r.trackingId || r.newWeight <= 0

  // ─── Render ───────────────────────────────────────────────────────────────

  const renderUploadStep = () => (
    <>
      {/* Mode tabs */}
      <div
        style={{
          display: 'flex',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          overflow: 'hidden',
          marginBottom: '24px',
        }}
      >
        {(
          [
            {
              key: 'preview',
              label: 'Auto Preview',
              icon: 'mdi:auto-fix',
              desc: 'trackingId + newWeight only — BE calculates prices',
            },
            {
              key: 'manual',
              label: 'Pre-calculated',
              icon: 'mdi:table-edit',
              desc: 'Full sheet with amounts already filled in',
            },
          ] as const
        ).map((m) => (
          <div
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              flex: 1,
              padding: '14px 16px',
              cursor: 'pointer',
              backgroundColor: mode === m.key ? '#446ce4' : '#f9f9f9',
              color: mode === m.key ? '#fff' : '#333',
              transition: 'all 0.2s',
              textAlign: 'center',
            }}
          >
            <Icon
              icon={m.icon}
              style={{
                fontSize: '22px',
                marginBottom: '4px',
                display: 'block',
                margin: '0 auto 4px',
              }}
            />
            <strong style={{ fontSize: '14px', display: 'block' }}>
              {m.label}
            </strong>
            <span style={{ fontSize: '12px', opacity: 0.85 }}>{m.desc}</span>
          </div>
        ))}
      </div>

      {/* Expected format hint */}
      <div
        style={{
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          fontSize: '13px',
        }}
      >
        <strong>Expected Excel columns:</strong>
        <code
          style={{
            display: 'block',
            marginTop: '8px',
            backgroundColor: '#fff',
            padding: '10px',
            borderRadius: '4px',
            fontFamily: 'monospace',
          }}
        >
          {mode === 'preview' ? (
            <>
              <span style={{ color: '#446ce4' }}>trackingId</span>
              {' | '}
              <span style={{ color: '#446ce4' }}>newWeight</span>
              <br />
              <span style={{ color: '#888' }}>QC123456789 | 5.5</span>
            </>
          ) : (
            <>
              <span style={{ color: '#446ce4' }}>trackingId</span>
              {' | '}
              <span style={{ color: '#446ce4' }}>newWeight</span>
              {' | '}
              <span style={{ color: '#446ce4' }}>amount</span>
              {' | '}
              <span style={{ color: '#888' }}>type</span>
              {' | '}
              <span style={{ color: '#888' }}>description</span>
              <br />
              <span style={{ color: '#888' }}>
                QC123456789 | 5.5 | 7500 | automatic | DHL weight report
              </span>
            </>
          )}
        </code>
      </div>

      {/* Drop zone */}
      {!file ? (
        <div
          style={{
            border: '2px dashed #ddd',
            borderRadius: '8px',
            padding: '40px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <Icon
            icon='mdi:cloud-upload-outline'
            style={{ fontSize: '48px', color: '#999', marginBottom: '12px' }}
          />
          <p style={{ margin: '0 0 6px', fontWeight: 500 }}>
            Click to upload or drag and drop
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: '#888' }}>
            .xlsx or .xls files only
          </p>
          <input
            ref={fileInputRef}
            type='file'
            accept='.xlsx,.xls'
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      ) : (
        <div
          style={{
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icon
              icon='mdi:file-excel'
              style={{ fontSize: '28px', color: '#4caf50' }}
            />
            <div>
              <p style={{ margin: 0, fontWeight: 500 }}>{file.name}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
          <button
            onClick={removeFile}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#f44336',
              fontSize: '20px',
            }}
          >
            <Icon icon='mdi:close' />
          </button>
        </div>
      )}

      <div className='modal-actions' style={{ marginTop: '24px' }}>
        <button className='btn-secondary' onClick={onClose}>
          Cancel
        </button>
        {loading ? (
          <FormLoader position='center' />
        ) : (
          <button
            className='btn-primary'
            onClick={mode === 'preview' ? handleAutoPreview : handleManualParse}
            disabled={!file}
          >
            <Icon icon='mdi:eye-outline' style={{ marginRight: '6px' }} />
            {mode === 'preview' ? 'Preview via BE' : 'Parse & Review'}
          </button>
        )}
      </div>
    </>
  )

  const renderReviewStep = () => (
    <>
      {/* Summary bar */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '16px',
          flexWrap: 'wrap',
        }}
      >
        {[
          { label: 'Total', value: reviewItems.length, color: '#446ce4' },
          { label: 'Valid', value: validCount, color: '#4caf50' },
          { label: 'Invalid', value: invalidCount, color: '#f44336' },
          ...(failedItems.length > 0
            ? [
                {
                  label: 'Preview Failed',
                  value: failedItems.length,
                  color: '#ff9800',
                },
              ]
            : []),
        ].map((s) => (
          <div
            key={s.label}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '13px', color: '#666' }}>{s.label}</span>
            <strong style={{ color: s.color }}>{s.value}</strong>
          </div>
        ))}
        <button
          onClick={() => {
            setStep('upload')
            removeFile()
          }}
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: '1px solid #ddd',
            borderRadius: '6px',
            padding: '6px 12px',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Icon icon='mdi:arrow-left' /> Back
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '16px',
          justifyContent: 'flex-end',
        }}
      >
        <button
          onClick={exportProcessedItems}
          disabled={reviewItems.length === 0}
          className='btn-secondary'
          style={{
            fontSize: '13px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icon icon='mdi:download-outline' /> Export Processed
        </button>
        <button
          onClick={removeZeroAmountItems}
          disabled={reviewItems.length === 0}
          className='btn-danger-outline'
          style={{
            fontSize: '13px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            border: '1px solid #f44336',
            color: '#f44336',
            backgroundColor: 'transparent',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          <Icon icon='mdi:filter-remove-outline' /> Remove Zero Amount
        </button>
      </div>

      {/* Editable table */}
      <div
        style={{
          overflowX: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          maxHeight: '360px',
          overflowY: 'auto',
          marginBottom: '16px',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: '#f5f5f5',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <th style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>
                #
              </th>
              <th style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>
                Tracking ID
              </th>
              {mode === 'preview' && (
                <>
                  <th
                    style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}
                  >
                    Old Wt.
                  </th>
                  <th
                    style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}
                  >
                    New Wt.
                  </th>
                  <th
                    style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}
                  >
                    Old Amt.
                  </th>
                  <th
                    style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}
                  >
                    New Amt.
                  </th>
                  <th
                    style={{
                      ...cellStyle,
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#446ce4',
                    }}
                  >
                    Amount to Charge ✎
                  </th>
                </>
              )}
              {mode === 'manual' && (
                <>
                  <th
                    style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}
                  >
                    New Wt. (kg)
                  </th>
                  <th
                    style={{
                      ...cellStyle,
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#446ce4',
                    }}
                  >
                    Amount ✎
                  </th>
                  <th
                    style={{
                      ...cellStyle,
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#446ce4',
                    }}
                  >
                    Type ✎
                  </th>
                </>
              )}
              <th
                style={{
                  ...cellStyle,
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#446ce4',
                }}
              >
                Description ✎
              </th>
              <th
                style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}
              ></th>
            </tr>
          </thead>
          <tbody>
            {reviewItems.map((row, i) => (
              <tr
                key={i}
                style={{
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: isRowInvalid(row) ? '#fff8f8' : undefined,
                }}
              >
                <td style={cellStyle}>{i + 1}</td>
                <td style={cellStyle}>
                  {isRowInvalid(row) && (
                    <Icon
                      icon='mdi:alert-circle'
                      style={{
                        color: '#f44336',
                        verticalAlign: 'middle',
                        marginRight: '4px',
                      }}
                    />
                  )}
                  <strong>
                    {row.trackingId || (
                      <em style={{ color: '#999' }}>missing</em>
                    )}
                  </strong>
                </td>
                {mode === 'preview' && (
                  <>
                    <td style={cellStyle}>{row.oldWeight ?? '-'} kg</td>
                    <td style={cellStyle}>{row.newWeight} kg</td>
                    <td style={cellStyle}>
                      {row.oldAmount != null
                        ? formatCurrency(row.oldAmount)
                        : '-'}
                    </td>
                    <td style={cellStyle}>
                      {row.newAmount != null
                        ? formatCurrency(row.newAmount)
                        : '-'}
                    </td>
                    <td style={cellStyle}>
                      <input
                        type='number'
                        style={inputStyle}
                        value={row.amount}
                        onChange={(e) =>
                          updateItem(i, 'amount', e.target.value)
                        }
                      />
                      {row.calculatedAmount != null &&
                        Number(row.amount) !== row.calculatedAmount && (
                          <span
                            style={{
                              fontSize: '11px',
                              color: '#ff9800',
                              display: 'block',
                            }}
                          >
                            suggested: {formatCurrency(row.calculatedAmount)}
                          </span>
                        )}
                    </td>
                  </>
                )}
                {mode === 'manual' && (
                  <>
                    <td style={cellStyle}>{row.newWeight} kg</td>
                    <td style={cellStyle}>
                      <input
                        type='number'
                        style={inputStyle}
                        value={row.amount}
                        onChange={(e) =>
                          updateItem(i, 'amount', e.target.value)
                        }
                      />
                    </td>
                    <td style={cellStyle}>
                      <select
                        style={{ ...inputStyle, minWidth: '100px' }}
                        value={row.type}
                        onChange={(e) => updateItem(i, 'type', e.target.value)}
                      >
                        <option value='manual'>manual</option>
                        <option value='automatic'>automatic</option>
                      </select>
                    </td>
                  </>
                )}
                <td style={cellStyle}>
                  <input
                    type='text'
                    style={{ ...inputStyle, minWidth: '180px' }}
                    value={row.description}
                    onChange={(e) =>
                      updateItem(i, 'description', e.target.value)
                    }
                  />
                </td>
                <td style={cellStyle}>
                  <button
                    onClick={() => removeItem(i)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#f44336',
                      padding: '2px',
                    }}
                    title='Remove row'
                  >
                    <Icon
                      icon='mdi:delete-outline'
                      style={{ fontSize: '18px' }}
                    />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Failed rows from preview (read-only) */}
      {failedItems.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <p
            style={{
              fontWeight: 600,
              fontSize: '14px',
              color: '#f44336',
              marginBottom: '8px',
            }}
          >
            <Icon
              icon='mdi:alert'
              style={{ verticalAlign: 'middle', marginRight: '6px' }}
            />
            {failedItems.length} item(s) failed preview (will not be submitted)
          </p>
          <button
            onClick={() => exportFailedItems(failedItems)}
            style={{
              marginBottom: '8px',
              fontSize: '13px',
              padding: '4px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              border: '1px solid #f44336',
              borderRadius: '4px',
              backgroundColor: '#fff',
              color: '#f44336',
            }}
          >
            <Icon icon='mdi:download' /> Export Failed Items
          </button>

          <div
            style={{
              overflowX: 'auto',
              border: '1px solid #ffd0d0',
              borderRadius: '6px',
              maxHeight: '150px',
              overflowY: 'auto',
              backgroundColor: '#fff8f8',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
              }}
            >
              <thead>
                <tr style={{ backgroundColor: '#ffebee' }}>
                  <th style={{ ...cellStyle, textAlign: 'left' }}>
                    Tracking ID
                  </th>
                  <th style={{ ...cellStyle, textAlign: 'left' }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {failedItems.map((f, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #ffd0d0' }}>
                    <td style={{ ...cellStyle, fontWeight: 500 }}>
                      {f.trackingId}
                    </td>
                    <td style={{ ...cellStyle, color: '#c62828' }}>
                      {f.error}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className='modal-actions'>
        <button
          className='btn-secondary'
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        {submitting ? (
          <FormLoader position='center' />
        ) : (
          <button
            className='btn-success'
            onClick={handleSubmit}
            disabled={validCount === 0}
          >
            <Icon
              icon='mdi:check-circle-outline'
              style={{ marginRight: '6px' }}
            />
            Submit {validCount} Discrepanc{validCount === 1 ? 'y' : 'ies'}
          </button>
        )}
      </div>
    </>
  )

  const renderDoneStep = () => (
    <>
      <div style={{ textAlign: 'center', padding: '20px 0 10px' }}>
        <Icon
          icon='mdi:check-circle'
          style={{ fontSize: '64px', color: '#4caf50', marginBottom: '12px' }}
        />
        <h3 style={{ marginBottom: '8px' }}>Bulk Upload Complete</h3>
        <p style={{ color: '#666' }}>
          Processed {result?.totalProcessed} item(s)
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            padding: '12px 24px',
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
          }}
        >
          <strong
            style={{ fontSize: '24px', color: '#4caf50', display: 'block' }}
          >
            {result?.successful.length ?? 0}
          </strong>
          <span style={{ fontSize: '13px', color: '#666' }}>Successful</span>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '12px 24px',
            backgroundColor: '#ffebee',
            borderRadius: '8px',
          }}
        >
          <strong
            style={{ fontSize: '24px', color: '#f44336', display: 'block' }}
          >
            {result?.failed.length ?? 0}
          </strong>
          <span style={{ fontSize: '13px', color: '#666' }}>Failed</span>
        </div>
      </div>

      {result && result.failed.length > 0 && (
        <div
          style={{
            overflowX: 'auto',
            border: '1px solid #ffd0d0',
            borderRadius: '6px',
            marginBottom: '20px',
            backgroundColor: '#fff8f8',
          }}
        >
          <p
            style={{
              fontWeight: 600,
              fontSize: '14px',
              padding: '10px 12px 4px',
              color: '#f44336',
            }}
          >
            Failed items
          </p>
          <button
            onClick={() => exportFailedItems(result?.failed || [])}
            style={{
              float: 'right',
              marginTop: '-30px',
              marginRight: '10px',
              fontSize: '12px',
              padding: '2px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              border: '1px solid #f44336',
              borderRadius: '4px',
              backgroundColor: '#fff',
              color: '#f44336',
            }}
          >
            <Icon icon='mdi:download' /> Export
          </button>

          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px',
            }}
          >
            <thead>
              <tr style={{ backgroundColor: '#ffebee' }}>
                <th style={{ ...cellStyle, textAlign: 'left' }}>Tracking ID</th>
                <th style={{ ...cellStyle, textAlign: 'left' }}>Error</th>
              </tr>
            </thead>
            <tbody>
              {result.failed.map((f, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #ffd0d0' }}>
                  <td style={{ ...cellStyle, fontWeight: 500 }}>
                    {f.trackingId}
                  </td>
                  <td style={{ ...cellStyle, color: '#c62828' }}>{f.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className='modal-actions'>
        <button className='btn-primary' onClick={onClose}>
          Done
        </button>
      </div>
    </>
  )

  return (
    <div className='modal-overlay'>
      <div
        className='modal-content'
        style={{ maxWidth: step === 'review' ? '960px' : '620px' }}
      >
        <div className='modal-header'>
          <h2>
            <Icon
              icon='mdi:upload-multiple'
              style={{ verticalAlign: 'middle', marginRight: '8px' }}
            />
            Bulk Upload Discrepancies
          </h2>
          <button className='close-btn' onClick={onClose}>
            <Icon icon='mdi:close' />
          </button>
        </div>

        <div className='modal-body'>
          {step === 'upload' && renderUploadStep()}
          {step === 'review' && renderReviewStep()}
          {step === 'done' && renderDoneStep()}
        </div>
      </div>
    </div>
  )
}

export default BulkUploadModal
